import { useContext } from "react";
import { Col, ColorPicker, Form, Row, Typography } from "antd";
import { Controller, useWatch } from "react-hook-form";

import { ICityFormStepProps } from "../../../../interfaces";
import { ICity } from "../../../../../../interfaces";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import PhoneMockupLogin from "../../../PhoneMockupLogin";
import PhoneMockupHome from "../../../PhoneMockupHome";

const { Title } = Typography;

interface IProps extends ICityFormStepProps {
    city: ICity | undefined;
}

export default function CustomStep({ control, errors, city }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const primaryColor = useWatch({ control, name: "primary_color" });
    const secondaryColor = useWatch({ control, name: "secondary_color" });
    const fontColor = useWatch({ control, name: "font_color" });
    const currentRaffleColor = useWatch({ control, name: "current_raffle_color" });
    const nextRaffleColor = useWatch({ control, name: "next_raffle_color" });

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
                alignItems: "flex-start",
                justifyContent: "center",
            }}
        >
            {/* Mockups Prévia */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 20,
                    width: "100%",
                }}
            >
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", minWidth: 300, justifyContent: "center" }}>
                    <PhoneMockupLogin
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        darkMode={false}
                        city={city}
                    />
                    <PhoneMockupHome
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        fontColor={fontColor}
                        currentRaffleColor={currentRaffleColor}
                        nextRaffleColor={nextRaffleColor}
                        city={city}
                    />
                </div>

                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", minWidth: 300, justifyContent: "center" }}>
                    <PhoneMockupLogin
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        darkMode={true}
                        city={city}
                    />
                    <PhoneMockupHome
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        darkMode={true}
                        fontColor={fontColor}
                        currentRaffleColor={currentRaffleColor}
                        nextRaffleColor={nextRaffleColor}
                        city={city}
                    />
                </div>
            </div>

            {/* Controles de Cores */}
            <div
                style={{
                    width: "100%",
                    padding: "0 10px",
                    borderRadius: 12,
                    marginTop: 10,
                }}
            >
                <Title level={4} style={{ color: isDark ? "#f0f0f0" : "#1e293b", marginBottom: 16 }}>
                    Personalização de Cor:
                </Title>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={5}>
                        <Form.Item
                            label="Cor Primária:"
                            validateStatus={errors.primary_color ? "error" : "success"}
                            help={errors.primary_color?.message}
                        >
                            <Controller
                                name="primary_color"
                                control={control}
                                defaultValue="#254da8"
                                render={({ field }) => (
                                    <ColorPicker
                                        value={field.value}
                                        onChange={(color) => field.onChange(color.toHexString())}
                                        showText
                                        disabledAlpha
                                        placement="top"
                                        format="hex"
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={5}>
                        <Form.Item
                            label="Cor Secundária:"
                            validateStatus={errors.secondary_color ? "error" : "success"}
                            help={errors.secondary_color?.message}
                        >
                            <Controller
                                name="secondary_color"
                                control={control}
                                defaultValue="#018757"
                                render={({ field }) => (
                                    <ColorPicker
                                        value={field.value}
                                        onChange={(color) => field.onChange(color.toHexString())}
                                        showText
                                        disabledAlpha
                                        placement="top"
                                        format="hex"
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item
                            label="Cor da Fonte:"
                            validateStatus={errors.font_color ? "error" : "success"}
                            help={errors.font_color?.message}
                        >
                            <Controller
                                name="font_color"
                                control={control}
                                defaultValue="#ffffff"
                                render={({ field }) => (
                                    <ColorPicker
                                        value={field.value}
                                        onChange={(color) => field.onChange(color.toHexString())}
                                        showText
                                        disabledAlpha
                                        placement="top"
                                        format="hex"
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={5}>
                        <Form.Item
                            label="Cor do Sorteio Atual:"
                            validateStatus={errors.current_raffle_color ? "error" : "success"}
                            help={errors.current_raffle_color?.message}
                        >
                            <Controller
                                name="current_raffle_color"
                                control={control}
                                defaultValue="#018757"
                                render={({ field }) => (
                                    <ColorPicker
                                        value={field.value}
                                        onChange={(color) => field.onChange(color.toHexString())}
                                        showText
                                        disabledAlpha
                                        placement="top"
                                        format="hex"
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={5}>
                        <Form.Item
                            label="Cor do Próximo Sorteio:"
                            validateStatus={errors.next_raffle_color ? "error" : "success"}
                            help={errors.next_raffle_color?.message}
                        >
                            <Controller
                                name="next_raffle_color"
                                control={control}
                                defaultValue="#018757"
                                render={({ field }) => (
                                    <ColorPicker
                                        value={field.value}
                                        onChange={(color) => field.onChange(color.toHexString())}
                                        showText
                                        disabledAlpha
                                        placement="top"
                                        format="hex"
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </div>
    );
}